"use client"
import * as React from "react"
import { useState, useEffect } from "react"
import { useAuth } from "@/context/AuthContext";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"

import { Input } from "@/components/ui/input"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { Toaster } from "@/components/ui/sonner"

export default function Page() {

  const [error, setError] = useState('');

  const { user, signIn } = useAuth()

  const searchParams = useSearchParams();
  const status = searchParams.get('status');

  const router = useRouter();

  useEffect(() => {
    if (status === "timed-out") {
      toast("Timed out!", {
        description: "Your session has timed out. Please sign in again.",
        duration: Infinity,
        action: {
          label: "Dismiss",
          onClick: () => {
            toast.dismiss()
          }
        },
      })
    }
    if (status === "signed-out") {
      toast("Signed out!", {
        description: "Your session has been succesfully closed out.",
        duration: Infinity,
        action: {
          label: "Dismiss",
          onClick: () => {
            toast.dismiss()
          }
        },
      })
    }
    if (status === "deleted") {
      toast("Account deleted!", {
        description: "Your account has successfully been deleted.",
        duration: Infinity,
        action: {
          label: "Dismiss",
          onClick: () => {
            toast.dismiss()
          }
        },
      })
    }
    if (status === "activated") {
      toast("Account activated!", {
        description: "Your account is now active. Please sign in to get started.",
        duration: Infinity,
        action: {
          label: "Dismiss",
          onClick: () => {
            toast.dismiss()
          }
        },
      })
    }
  }, [status])

  // redirect to home if the user is already signed in
  useEffect(() => {
    if (user) {
      router.push("/dashboard");
    }
  }, [user, router]);

  useEffect(() => {
    if (error) {
      toast("Problem!", {
        description: error,
        duration: Infinity,
        action: {
          label: "Dismiss",
          onClick: () => {
            toast.dismiss()
            setError('')
          }
        },
      })
    }
  }, [error]); 

  const handleSignIn = (email: string, password: string) => {
    signIn({email, password});
  };

  const onSubmit = (values: { email: string; password: string }) => {
    if (values.email === '') {
      setError('Please enter your email.')
    }
    else if (values.password === '') {
      setError('Please enter your password.')
    } else {
      handleSignIn(values.email, values.password);
    }
  };

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const page =
    <div className="w-full flex flex-col justify-center items-center mt-32 px-6">
      <Card className="md:min-w-sm">
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
          <CardDescription>
            The beta version of LawLibrary.AI is currently invite-only. If you have an invite code, please sign up to get started.
          </CardDescription>
        </CardHeader>
        <CardContent>
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 gap-4">

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Password</FormLabel>
                      <FormControl>
                        <Input type="password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              <Button type="submit" size="lg" className="mt-2">
                Sign in
              </Button>
              </div>
            </form>
            <div className="mt-4 text-center text-sm font-normal text-muted-foreground">
              Don&apos;t have an account?{" "}
              <Link href="/sign-up" className="underline">
                Sign up
              </Link>
            </div>
          </Form>
        </CardContent>
      </Card>
      <Toaster />
    </div>

  return user ? null : page;
}
